import { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { useAppContext } from "../context/AppContext";
import { AppActionTypes } from "../context/AppContext";
import { apiService } from "../services/apiService";

/**
 * Hook centralizzato per la gestione tenants / keys / value
 */

export function useAppManager() {
  const { state, dispatch } = useAppContext();
  const {
    authToken,
    userActions,
    tenants,
    selectedTenant,
    keys,
    selectedKey,
    selectedValue,
    isLoadingKeys,
    isLoadingValue,
    keysError,
    valueError,
  } = state;

  const lastSelectedTenantRef = useRef(null);
  const mountedRef = useRef(true);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchTenants = useCallback(async () => {
    if (!authToken) return;
    try {
      const data = await apiService.fetchTenants();
      const found = data?.tenants?.all ?? [];
      dispatch({ type: AppActionTypes.SET_TENANTS, payload: found });

      if (found.length === 1) {
        dispatch({
          type: AppActionTypes.SET_SELECTED_TENANT,
          payload: found[0],
        });
        const userActions = await apiService.getUserActions(found[0].name);
        if (Array.isArray(userActions)) {
          dispatch({
            type: AppActionTypes.SET_USER_ACTIONS,
            payload: userActions,
          });
        }
      }
    } catch (error) {
      // non blocchiamo l'app: loggare solo in dev
      if (process.env.NODE_ENV === "development")
        console.error("fetchTenants:", error);

      dispatch({
        type: AppActionTypes.SET_USER_ACTIONS,
        payload: [],
      });
    }
  }, [authToken, dispatch]);

  const selectTenant = useCallback(
    async (tenant) => {
      if (selectedTenant?.name === tenant?.name) return;
      try {
        const userActions = await apiService.getUserActions(tenant?.name);
        if (Array.isArray(userActions)) {
          dispatch({
            type: AppActionTypes.SET_USER_ACTIONS,
            payload: userActions,
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching user roles:", error);
        }
        dispatch({
          type: AppActionTypes.SET_USER_ACTIONS,
          payload: [],
        });
      }

      dispatch({ type: AppActionTypes.SET_SELECTED_TENANT, payload: tenant });
      // reset data on tenant change
      dispatch({ type: AppActionTypes.SET_KEYS, payload: [] });
      dispatch({ type: AppActionTypes.SET_SELECTED_KEY, payload: null });
      dispatch({ type: AppActionTypes.SET_SELECTED_VALUE, payload: null });
    },
    [dispatch, selectedTenant]
  );

  const fetchKeys = useCallback(
    async (reset = true) => {
      if (!selectedTenant) return;
      // Reset keys and cursor if this is a fresh fetch
      if (reset) {
        dispatch({ type: AppActionTypes.SET_KEYS, payload: [] });
      }

      // Do nothing if we're at the end and this is a loadMore call
      if (!reset && keys.length > 0) {
        dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        return;
      }

      dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: true });
      dispatch({ type: AppActionTypes.SET_KEYS_ERROR, payload: null });

      try {
        const data = await apiService.fetchAllItems(selectedTenant.name);
        let allKeys;
        if (reset) {
          allKeys = data.items || [];
          dispatch({ type: AppActionTypes.SET_KEYS, payload: allKeys });
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        } else {
          allKeys = [...keys, ...(data.items || [])];
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        }
      } catch (error) {
        console.error("Error fetching keys:", error);
        dispatch({
          type: AppActionTypes.SET_KEYS_ERROR,
          payload: error.message,
        });
      } finally {
        dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
      }
    },
    [selectedTenant, dispatch, keys]
  );

  const selectKey = useCallback(
    async (key) => {
      if (!selectedTenant) return;
      const keyId =
        typeof key === "string"
          ? key
          : key && typeof key === "object"
          ? key.key ?? key.full_key ?? key.id ?? null
          : null;
      dispatch({ type: AppActionTypes.SET_SELECTED_KEY, payload: keyId });
      dispatch({ type: AppActionTypes.SET_LOADING_VALUE, payload: true });
      dispatch({ type: AppActionTypes.SET_VALUE_ERROR, payload: null });

      try {
        const data = await apiService.fetchValue(selectedTenant.name, keyId);
        console.log("fetchValue returned:", data);

        dispatch({ type: AppActionTypes.SET_SELECTED_VALUE, payload: data });
      } catch (error) {
        console.error("Error fetching value:", error);
        dispatch({
          type: AppActionTypes.SET_VALUE_ERROR,
          payload: error?.message || String(error),
        });
      } finally {
        dispatch({ type: AppActionTypes.SET_LOADING_VALUE, payload: false });
      }
    },
    [selectedTenant, dispatch]
  );

  const updateKeyValue = useCallback(
    async (newValue) => {
      if (!selectedTenant || !selectedKey) return;

      setIsUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(false);

      try {
        await apiService.updateValue(
          selectedTenant.name,
          selectedKey,
          newValue
        );
        const updatedValueData = await apiService.fetchValue(
          selectedTenant.name,
          selectedKey
        );

        dispatch({
          type: AppActionTypes.SET_SELECTED_VALUE,
          payload: updatedValueData,
        });

        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateSuccess(false);
        }, 3000);
        await fetchKeys(true);
      } catch (error) {
        console.error("Error updating value:", error);
        setUpdateError(error.message);
      } finally {
        setIsUpdating(false);
      }
    },
    [selectedTenant, selectedKey, dispatch, fetchKeys]
  );

  const getQrCodeImage = useCallback(
    (rinaId, tenant) => apiService.getQrCodeImage(rinaId, tenant),
    []
  );
  const getKeyById = useCallback(
    async (keyId) => {
      if (!selectedTenant?.name || !keyId) return;
      dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: true });
      dispatch({ type: AppActionTypes.SET_KEYS_ERROR, payload: null });
      try {
        dispatch({ type: AppActionTypes.SET_KEYS, payload: [] });
        const data = await apiService.fetchValue(selectedTenant.name, keyId);
        if (data) {
          const keyObject = {
            key: keyId,
            full_key: keyId,
            tenant: selectedTenant.name,
            type: data?.type ?? "String",
          };
          dispatch({ type: AppActionTypes.SET_KEYS, payload: [keyObject] });
          await selectKey(keyObject);
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        } else {
          dispatch({ type: AppActionTypes.SET_KEYS, payload: [] });
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          dispatch({ type: AppActionTypes.SET_KEYS, payload: [] });
          dispatch({ type: AppActionTypes.SET_KEYS_ERROR, payload: null });
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        } else {
          dispatch({
            type: AppActionTypes.SET_KEYS_ERROR,
            payload: error?.message ?? String(error),
          });
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        }
        if (process.env.NODE_ENV === "development") {
          console.error("getKeyById:", error);
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
        }
      } finally {
        if (mountedRef.current)
          dispatch({ type: AppActionTypes.SET_LOADING_KEYS, payload: false });
      }
    },
    [selectedTenant, dispatch, selectKey]
  );


  const createKey = useCallback(
    async (keyId, value) => {
      if (!selectedTenant?.name)
        return { success: false, error: "No tenant selected" };
      try {
        const result = await apiService.createKey(
          selectedTenant.name,
          keyId,
          value
        );
        await fetchKeys(true);
        return { success: true, data: result };
      } catch (error) {
        if (process.env.NODE_ENV === "development")
          console.error("createKey:", error);
        return { success: false, error: error?.message ?? String(error) };
      }
    },
    [selectedTenant, fetchKeys]
  );

  // effects
  useEffect(() => {
    if (authToken) fetchTenants();
  }, [authToken, fetchTenants]);

  useEffect(() => {
    if (
      selectedTenant?.name &&
      lastSelectedTenantRef.current !== selectedTenant.name
    ) {
      lastSelectedTenantRef.current = selectedTenant.name;
      const id = setTimeout(() => fetchKeys(true), 100);
      return () => clearTimeout(id);
    }
  }, [selectedTenant, fetchKeys]);

  // memoize return to keep API stable
  return useMemo(
    () => ({
      userActions,
      tenants,
      selectedTenant,
      keys,
      selectedKey,
      selectedValue,
      isLoadingKeys,
      isLoadingValue,
      keysError,
      valueError,
      selectTenant,
      fetchKeys,
      selectKey,
      getQrCodeImage,
      updateKeyValue,
      isUpdating,
      updateError,
      updateSuccess,
      getKeyById,
      createKey,
    }),
    [
      userActions,
      tenants,
      selectedTenant,
      keys,
      selectedKey,
      selectedValue,
      isLoadingKeys,
      isLoadingValue,
      keysError,
      valueError,
      selectTenant,
      fetchKeys,
      selectKey,
      getQrCodeImage,
      updateKeyValue,
      isUpdating,
      updateError,
      updateSuccess,
      getKeyById,
      createKey,
    ]
  );
}
